import React, { type ReactNode } from "react";
import Image, { type StaticImageData } from "next/image";
import { Text, SimpleGrid, Box, VStack, Flex, AspectRatio, type AspectRatioProps } from "@chakra-ui/react";
import { Section } from "components/section";
import { Link, ArrowLink } from "ui/link";
import firstPairImage from "../../public/assets/company/about/pair-1.png";
import secondPairImage from "../../public/assets/company/about/pair-2.png";

export function AboutHightouch() {
  return (
    <>
      <Section>
        <AboutPair
          align="right"
          image={firstPairImage}
          imageAlt="Data moving from a data warehouse into various tools with Highotouch."
          imageRatio={3 / 2}
          title="About the product"
          actionHref="/demo"
          actionTitle="Book a demo"
          content={
            <Text>
              Hightouch syncs data from any data warehouse into the tools that your business runs on. With the power of
              SQL or{" "}
              <Link variant="article" href="/platform/customer-studio">
                Customer Studio
              </Link>
              , an entire suite of no-code features, anyone in your organization can explore customer data and sync it
              into marketing, advertising, sales, and customer success tools, without engineering effort.
            </Text>
          }
        />
      </Section>

      <Section>
        <AboutPair
          align="left"
          image={secondPairImage}
          imageAlt="Hightouch comapny photos."
          imageRatio={3 / 4}
          title="About the company"
          actionHref="/careers"
          actionTitle="View open positions"
          content={
            <>
              <Text>
                We are a Series B startup backed by top investors including Amplify Partners, Bain Capital Ventures,
                ICONIQ Growth, and Y Combinator. Angel investors include the cofounders of Segment, Datadog, Okta, and
                Clearbit. Some of our customers include Autotrader, Plaid, Blend, Retool, Ramp, and CircleCI.
              </Text>
              <Text>
                Our founders were early employees of Segment where they saw the transformative impact of enabling
                business and marketing teams to activate customer data. However, they also recognized the limitations of
                the existing rigid legacy Customer Data Platform (CDP) market.
              </Text>
              <Text>
                By building a platform that could sit on top of an organization’s existing data infrastructure, our
                founders knew that teams could unlock a more performant, flexible and secure solution to activating
                customer data at scale - and thus, Hightouch was born.
              </Text>

              <Text>
                With a relentless drive for innovation and a deep understanding of the evolving digital marketing
                landscape, Hightouch is changing the way that today’s organizations leverage their customer data. We are
                committed to providing a cutting-edge platform that empowers everyone within an organization to activate
                their data at scale to drive their business forward.
              </Text>
            </>
          }
        />
      </Section>
    </>
  );
}

type AboutPairProps = {
  align: "left" | "right";
  title: string;
  content: ReactNode;
  actionTitle: string;
  actionHref: string;
  image: StaticImageData;
  imageAlt?: string;
  imageRatio: AspectRatioProps["ratio"];
};

function AboutPair({ align, title, content, actionTitle, actionHref, image, imageAlt, imageRatio }: AboutPairProps) {
  return (
    <SimpleGrid
      columns={{ base: 1, lg: 2 }}
      gap={{ base: 8, md: 12, lg: 24 }}
      alignItems={{ base: "start", lg: "stretch" }}
    >
      <Box order={{ base: 1, lg: align === "left" ? 1 : 2 }}>
        <Text as="h3" mb={7} textStyle="h4" whiteSpace="pre-wrap">
          {title}
        </Text>
        <VStack spacing={7} mb={7} align="flex-start" fontSize="lg">
          {content}
        </VStack>

        <ArrowLink href={actionHref} size="lg" variant="grass">
          {actionTitle}
        </ArrowLink>
      </Box>

      <Flex order={{ base: 1, lg: align === "left" ? 2 : 1 }} direction="column" w="100%">
        <AspectRatio
          ratio={imageRatio}
          pos="relative"
          w="100%"
          maxW={{ base: "100%", md: "500px", lg: "100%" }}
          maxH={{ base: "500px", md: "600px", lg: "100%" }}
          mx="auto"
        >
          <Image fill src={image} alt={imageAlt ?? title} style={{ objectFit: "contain" }} />
        </AspectRatio>
      </Flex>
    </SimpleGrid>
  );
}
